import { useNavigate } from '@tanstack/react-router';
import {
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useRef, useState } from 'react';
import { PAGE_LAYOUT_FRAME_HEIGHT } from '@/shared/lib/constants';
import { DEFAULT_SEARCH, type PageSize } from '@/shared/lib/route';
import { useSkeletonMinDisplay } from '@/shared/ui';
import { DisplaySettingsPopover } from '@/shared/ui/display-settings-popover/DisplaySettingsPopover';
import { useNestedStickyOffset } from '../helpers/nested-sticky-context';
import type { DataTableProps, StickyConfig } from '../helpers/types';
import { useScrollToRow, useTableColumnStyles } from '../hooks';
import { useNestedPinnedTransform } from '../hooks/useNestedPinnedTransform';
import { CNTable } from './CNTable';
import { Pagination } from './Pagination';
import { StickyTableWrapper } from './StickyTableWrapper';
import { TableBody } from './TableBody';
import { TableColgroup } from './TableColgroup';
import { TableDataRows } from './TableDataRows';
import { TableEmptyRow } from './TableEmptyRow';
import { TableHeader } from './TableHeader';
import { TableHeaderRows } from './TableHeaderRows';
import { TableSkeletonRows } from './TableSkeletonRows';
import { VirtualTableBody } from './VirtualTableBody';

/**
 * TanStack Tableを使用したデータテーブルコンポーネント
 */
export const Table = <TData, TValue>({
  columns,
  data,
  isLoading = false,
  pagination,
  expanded: controlledExpanded,
  onExpandedChange,
  expandable,
  showLastBorderBottom = true,
  displaySettings,
  emptyRender,
  highlightedRowIds,
  selectedRowIds,
  scrollToRowId,
  sticky,
  filterPopover,
  filterTags,
  rightActionChildren,
  bulkActions,
  selectionBar,
  virtualScroll,
}: DataTableProps<TData, TValue>) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const navigate = useNavigate();

  // サーバーサイドページネーションモードかどうか
  const isServerSidePagination = pagination?.totalCount !== undefined;

  const [internalExpanded, setInternalExpanded] = useState<ExpandedState>({});

  const expanded = controlledExpanded ?? internalExpanded;

  // スケルトン行のIDを生成（リンターエラー回避のため）
  const skeletonRowIds = Array.from(
    { length: pagination?.pageSize ?? 1 },
    (_, i) => `skeleton-${i}`,
  );

  // 最低表示時間（0.5秒）を管理するカスタムフック
  const showSkeleton = useSkeletonMinDisplay(isLoading);

  // ページネーションが有効かどうか
  const isPaginationEnabled = pagination !== null && pagination !== undefined;

  const pageCount =
    isServerSidePagination && pagination?.pageSize && pagination.totalCount
      ? Math.ceil(pagination.totalCount / pagination.pageSize)
      : undefined;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // ページネーション未設定時はgetPaginationRowModelを使わず全件表示
    ...(isPaginationEnabled && { getPaginationRowModel: getPaginationRowModel() }),
    getExpandedRowModel: getExpandedRowModel(),
    enableExpanding: true,
    ...(expandable?.getSubRows && { getSubRows: expandable.getSubRows }),
    ...(expandable?.getRowId && { getRowId: expandable.getRowId }),
    manualPagination: isServerSidePagination,
    // ページネーション状態はURL（外部）で管理するため、TanStack Tableの自動リセットを無効化
    // ローディング中にtotalCountがundefinedになりmanualPaginationがfalseに切り替わっても、
    // ページが1にリセットされることを防ぐ
    ...(isPaginationEnabled && { autoResetPageIndex: false }),
    ...(pageCount !== undefined && { pageCount }),
    ...(isPaginationEnabled && {
      onPaginationChange: (updater) => {
        // propsのpage（1ベース）を内部のpageIndex（0ベース）に変換
        const currentPageIndex = (pagination?.page ?? 1) - 1;
        const pageSize = pagination?.pageSize ?? DEFAULT_SEARCH.pageSize;
        const currentPagination = {
          pageIndex: currentPageIndex,
          pageSize,
        };
        const newPagination = typeof updater === 'function' ? updater(currentPagination) : updater;
        navigate({
          to: '.',
          search: (prev) => ({
            ...prev,
            page: newPagination.pageIndex + 1, // pageIndexは0ベース、URLは1ベース
            page_size: newPagination.pageSize as PageSize,
          }),
        });
      },
    }),
    onExpandedChange: (updater) => {
      const newExpanded = typeof updater === 'function' ? updater(expanded) : updater;
      if (onExpandedChange) {
        onExpandedChange(newExpanded);
      } else {
        setInternalExpanded(newExpanded);
      }
    },
    state: {
      // ページネーション未設定時はstate.paginationを設定しない
      ...(isPaginationEnabled && {
        pagination: {
          pageIndex: (pagination?.page ?? 1) - 1,
          pageSize: pagination?.pageSize ?? DEFAULT_SEARCH.pageSize,
        },
      }),
      expanded,
    },
  });

  // カラムスタイル計算用フック
  const { columnStyles, weightedLeafWidthStyles, tableFixedWidth, pinnedColumnStyles } =
    useTableColumnStyles(table);

  // ヘッダー情報の取得
  const headerGroups = table.getHeaderGroups();
  const firstHeaderGroup = headerGroups[0];
  const leafColumns = table.getAllLeafColumns();
  const rows = table.getRowModel().rows;

  // ネストされたテーブルの場合、親テーブルからsticky設定を取得
  const nestedStickyOffset = useNestedStickyOffset();

  // ネストされたテーブルでstickyが有効な場合、CSS position: stickyを使用
  const isNestedSticky = !!sticky && !!nestedStickyOffset;

  // ネストテーブルのピン留めセルをtransformで位置補正
  useNestedPinnedTransform({
    tableRef,
    proxyRef: nestedStickyOffset?.proxyRef ?? { current: null },
    containerRef: nestedStickyOffset?.containerRef ?? { current: null },
  });

  // Sticky設定の正規化（boolean | StickyConfigをStickyConfig | undefinedに変換）
  // ネストテーブルの場合はStickyTableWrapperを使わないため、stickyConfigはundefined
  const stickyConfig: StickyConfig | undefined =
    sticky && !isNestedSticky
      ? typeof sticky === 'boolean'
        ? { offsetHeader: PAGE_LAYOUT_FRAME_HEIGHT }
        : sticky
      : undefined;

  // 仮想スクロールモードかどうか（通常モードでのみ有効）
  const isVirtualScrollActive = !!virtualScroll && !stickyConfig && !isNestedSticky;

  // スクロール処理用フック（仮想スクロール時は VirtualTableBody 内で処理）
  useScrollToRow(isVirtualScrollActive ? null : scrollToRowId, tableRef);

  // テーブルボディのコンテンツ（スケルトン、データ行、空行）
  const tableBodyContent = showSkeleton ? (
    <TableSkeletonRows
      skeletonRowIds={skeletonRowIds}
      headers={firstHeaderGroup?.headers ?? []}
      columnStyles={columnStyles}
      weightedLeafWidthStyles={weightedLeafWidthStyles}
      pinnedColumnStyles={pinnedColumnStyles}
    />
  ) : rows.length ? (
    <TableDataRows
      rows={rows}
      columns={columns}
      columnStyles={columnStyles}
      weightedLeafWidthStyles={weightedLeafWidthStyles}
      pinnedColumnStyles={pinnedColumnStyles}
      showLastBorderBottom={showLastBorderBottom}
      expandable={expandable}
      highlightedRowIds={highlightedRowIds}
      selectedRowIds={selectedRowIds}
    />
  ) : (
    <TableEmptyRow
      colSpan={columns.length}
      showLastBorderBottom={showLastBorderBottom}
      emptyRender={emptyRender}
    />
  );

  // テーブルのレンダリング（stickyの有無で分岐）
  const renderTable = () => {
    // 仮想スクロールモード（React Virtuoso）
    if (isVirtualScrollActive) {
      const virtualHeight =
        typeof virtualScroll.height === 'number'
          ? `${virtualScroll.height}px`
          : virtualScroll.height;
      const tableMinWidthStyle = tableFixedWidth ? { minWidth: `${tableFixedWidth}px` } : undefined;

      if (showSkeleton || !rows.length) {
        return (
          <div style={{ height: virtualHeight }}>
            <CNTable style={tableMinWidthStyle}>
              <TableColgroup
                columns={leafColumns}
                columnStyles={columnStyles}
                weightedLeafWidthStyles={weightedLeafWidthStyles}
              />
              <TableHeader>
                <TableHeaderRows
                  headerGroups={headerGroups}
                  columnStyles={columnStyles}
                  weightedLeafWidthStyles={weightedLeafWidthStyles}
                  pinnedColumnStyles={pinnedColumnStyles}
                />
              </TableHeader>
              <TableBody>
                {showSkeleton ? (
                  <TableSkeletonRows
                    skeletonRowIds={skeletonRowIds}
                    headers={firstHeaderGroup?.headers ?? []}
                    columnStyles={columnStyles}
                    weightedLeafWidthStyles={weightedLeafWidthStyles}
                    pinnedColumnStyles={pinnedColumnStyles}
                  />
                ) : (
                  <TableEmptyRow
                    colSpan={columns.length}
                    showLastBorderBottom={showLastBorderBottom}
                    emptyRender={emptyRender}
                  />
                )}
              </TableBody>
            </CNTable>
          </div>
        );
      }

      return (
        <VirtualTableBody
          rows={rows}
          columns={columns}
          columnStyles={columnStyles}
          weightedLeafWidthStyles={weightedLeafWidthStyles}
          pinnedColumnStyles={pinnedColumnStyles}
          showLastBorderBottom={showLastBorderBottom}
          expandable={expandable}
          highlightedRowIds={highlightedRowIds}
          selectedRowIds={selectedRowIds}
          virtualScrollConfig={virtualScroll}
          scrollToRowId={scrollToRowId}
          tableMinWidth={tableFixedWidth}
          renderHeaderContent={() => (
            <TableHeaderRows
              headerGroups={headerGroups}
              columnStyles={columnStyles}
              weightedLeafWidthStyles={weightedLeafWidthStyles}
              pinnedColumnStyles={pinnedColumnStyles}
            />
          )}
          renderColgroup={() => (
            <TableColgroup
              columns={leafColumns}
              columnStyles={columnStyles}
              weightedLeafWidthStyles={weightedLeafWidthStyles}
            />
          )}
        />
      );
    }

    if (stickyConfig) {
      // Stickyモード: ヘッダーとボディを分離（トップレベルテーブル用）
      return (
        <StickyTableWrapper
          headerGroups={headerGroups}
          leafColumns={leafColumns}
          columnStyles={columnStyles}
          weightedLeafWidthStyles={weightedLeafWidthStyles}
          pinnedColumnStyles={pinnedColumnStyles}
          tableFixedWidth={tableFixedWidth}
          stickyConfig={stickyConfig}
          bodyTableRef={tableRef}
        >
          {tableBodyContent}
        </StickyTableWrapper>
      );
    }

    if (isNestedSticky) {
      // ネストStickyモード: CSS position: sticky でヘッダーを固定
      // body コンテナの overflow-x: auto が除去されたため、CSS sticky が正常に動作する
      return (
        <div>
          <CNTable
            ref={tableRef}
            style={tableFixedWidth ? { minWidth: `${tableFixedWidth}px` } : undefined}
          >
            <TableColgroup
              columns={leafColumns}
              columnStyles={columnStyles}
              weightedLeafWidthStyles={weightedLeafWidthStyles}
            />
            <TableHeader
              className="bg-border-subtle1"
              style={{
                position: 'sticky',
                top: nestedStickyOffset?.offsetHeader ?? 0,
                zIndex: nestedStickyOffset?.zIndex ?? 24,
              }}
            >
              <TableHeaderRows
                headerGroups={headerGroups}
                columnStyles={columnStyles}
                weightedLeafWidthStyles={weightedLeafWidthStyles}
                pinnedColumnStyles={pinnedColumnStyles}
              />
            </TableHeader>
            <TableBody>{tableBodyContent}</TableBody>
          </CNTable>
        </div>
      );
    }

    // 通常モード
    return (
      <div className="overflow-x-auto">
        <CNTable
          ref={tableRef}
          style={tableFixedWidth ? { minWidth: `${tableFixedWidth}px` } : undefined}
        >
          <TableColgroup
            columns={leafColumns}
            columnStyles={columnStyles}
            weightedLeafWidthStyles={weightedLeafWidthStyles}
          />
          <TableHeader>
            <TableHeaderRows
              headerGroups={headerGroups}
              columnStyles={columnStyles}
              weightedLeafWidthStyles={weightedLeafWidthStyles}
              pinnedColumnStyles={pinnedColumnStyles}
            />
          </TableHeader>
          <TableBody>{tableBodyContent}</TableBody>
        </CNTable>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {(filterPopover || displaySettings || rightActionChildren || filterTags || bulkActions) && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center justify-start gap-2">
              {filterPopover && filterPopover}
              {displaySettings && (
                <DisplaySettingsPopover
                  pageSize={pagination?.pageSize}
                  hasCustomSettings={
                    typeof displaySettings !== 'boolean' ? displaySettings.hasCustomSettings : false
                  }
                >
                  {typeof displaySettings !== 'boolean' ? displaySettings.children : undefined}
                </DisplaySettingsPopover>
              )}
              {bulkActions && bulkActions}
            </div>
            {rightActionChildren && rightActionChildren}
          </div>
          {filterTags && filterTags}
        </div>
      )}
      {selectionBar && selectionBar}
      {renderTable()}
      {pagination && (
        <Pagination
          pagination={pagination}
          totalCount={pagination.totalCount ?? data.length}
          isLoading={showSkeleton}
          onPageChange={(page) => {
            navigate({
              to: '.',
              search: (prev) => ({
                ...prev,
                page,
                page_size: (pagination.pageSize ?? DEFAULT_SEARCH.pageSize) as PageSize,
              }),
            });
          }}
        />
      )}
    </div>
  );
};
